/// <reference lib="dom" />
/**
 * Canvas2D draw helpers for enemy sprites — config lookup, special effects.
 * Extracted from webSpriteDraw.web.ts to keep files under the 300-line limit.
 *
 * Web-only (.web.ts) — never bundled on native. Read-only, no gameState mutation.
 *
 * @module webEnemyDraw
 */

import type { Enemy } from '../engine/gameState';
import {
  NIBBLER_SHEET,
  NIBBLER_CLIPS,
  NIBBLER_DRAW_OFFSET,
  THORNHOP_SHEET,
  THORNHOP_CLIPS,
  THORNHOP_DRAW_OFFSET,
  GLOOM_BAT_SHEET,
  GLOOM_BAT_CLIPS,
  GLOOM_BAT_DRAW_OFFSET,
  advanceAnim,
  type AnimClip,
  type SpriteSheet,
  type DrawOffset,
  type NibblerAnimState,
  type ThornhopAnimState,
  type GloomBatAnimState,
} from './SpriteRenderer';
import { type AnimState, getNormalizedSheet, drawSpriteFrame } from './webSpriteDraw';

/**
 * Pick the sprite sheet, clip, and image for an enemy by its constructor name.
 * Returns null for unknown enemy types — caller falls back to greybox.
 */
function getEnemySpriteConfig(
  enemy: Enemy,
  nibblerImg: HTMLImageElement,
  thornhopImg: HTMLImageElement,
  gloomBatImg: HTMLImageElement,
): { img: HTMLImageElement; sheet: SpriteSheet; clip: AnimClip; drawOffset: DrawOffset } | null {
  // DECISION: Use entityType tag instead of constructor.name — class names
  // are mangled in production builds, breaking sprite selection.
  const eName = (enemy as { entityType?: string }).entityType;
  if (eName === 'Nibbler') {
    // DECISION: Map entity NibblerState to NibblerAnimState.
    // SQUISHED → DEAD clip (squish transform applied by drawEnemySprite).
    // PATROL stays PATROL. HURT (shard) → HURT.
    const stateToAnim: Record<string, NibblerAnimState> = {
      PATROL: 'PATROL', SQUISHED: 'DEAD', DEAD: 'DEAD', HURT: 'HURT',
    };
    const animKey = stateToAnim[enemy.state as string] ?? 'IDLE';
    const clip = NIBBLER_CLIPS[animKey];
    return { img: nibblerImg, sheet: NIBBLER_SHEET, clip, drawOffset: NIBBLER_DRAW_OFFSET };
  }
  if (eName === 'Thornhop') {
    // DECISION: Map entity ThornhopState → ThornhopAnimState.
    // JUMPING with negative vy → RISE, positive vy → FALL.
    const tState = enemy.state as string;
    let animKey: ThornhopAnimState;
    if (tState === 'JUMPING') {
      // DECISION: Differentiate rise vs fall by velocity direction.
      const vy = (enemy as { vy?: number }).vy ?? 0;
      animKey = vy < 0 ? 'RISE' : 'FALL';
    } else {
      const stateToAnim: Record<string, ThornhopAnimState> = {
        IDLE: 'IDLE', LANDING: 'LAND', DEAD: 'DEAD',
      };
      animKey = stateToAnim[tState] ?? 'IDLE';
    }
    const clip = THORNHOP_CLIPS[animKey];
    return { img: thornhopImg, sheet: THORNHOP_SHEET, clip, drawOffset: THORNHOP_DRAW_OFFSET };
  }
  if (eName === 'GloomBat') {
    // DECISION: Entity states differ from sprite anim names. Map explicitly.
    // DIVING uses FLY (animated flapping) rather than the static DIVE frame
    // because at 32×29 render size the tucked-wing dive pose loses visible
    // wing detail, making the bat appear wingless.
    const stateToAnim: Record<string, GloomBatAnimState> = {
      HOVER: 'FLY', TELEGRAPH: 'AGGRO', DIVING: 'FLY',
      RECOVERING: 'FLY', DEAD: 'DEAD',
    };
    const animKey = stateToAnim[enemy.state as string] ?? 'FLY';
    const clip = GLOOM_BAT_CLIPS[animKey];
    return { img: gloomBatImg, sheet: GLOOM_BAT_SHEET, clip, drawOffset: GLOOM_BAT_DRAW_OFFSET };
  }
  return null;
}

/**
 * Draw one enemy with its current animation frame, advancing the supplied
 * AnimState by `dt` seconds. Falls back to a coloured rectangle for unknown
 * enemy types so the renderer never silently drops an entity.
 */
export function drawEnemySprite(
  ctx: CanvasRenderingContext2D,
  enemy: Enemy,
  anim: AnimState,
  dt: number,
  camX: number,
  camY: number,
  nibblerImg: HTMLImageElement,
  thornhopImg: HTMLImageElement,
  gloomBatImg: HTMLImageElement,
): void {
  const cfg = getEnemySpriteConfig(enemy, nibblerImg, thornhopImg, gloomBatImg);
  if (!cfg) {
    ctx.fillStyle = enemy.color;
    ctx.fillRect(Math.round(enemy.x - camX), Math.round(enemy.y - camY), enemy.width, enemy.height);
    return;
  }
  const next = advanceAnim(anim.elapsed, cfg.clip, dt);
  anim.elapsed = next.elapsed;
  anim.frameIndex = next.frameIndex;
  
  // DECISION: Treat missing facingRight as true.
  let isFacingRight = !('facingRight' in enemy) || (enemy as { facingRight?: boolean }).facingRight !== false;
  // DECISION: All enemy sheets (Nibbler, Thornhop v6, GloomBat v3) natively
  // face RIGHT — no per-enemy inversion needed.
  const eName = (enemy as { entityType?: string }).entityType;

  // DECISION: Thornhop's sheet is pre-centered by the build script with 94px+
  // padding per side. The normalisation pass (designed for messy AI sheets)
  // was shifting content across frame boundaries, causing visible bleed.
  // Skip normalisation for Thornhop — use the raw <img> element directly.
  const sheet: HTMLImageElement | HTMLCanvasElement = eName === 'Thornhop'
    ? cfg.img
    : getNormalizedSheet(cfg.img, cfg.sheet.frameWidth, cfg.sheet.frameHeight);

  // DECISION: Use drawOffset (visual size) instead of enemy.width/height
  // (hitbox size). Hitboxes are smaller than the visual sprite.
  const { drawOffset: dOff } = cfg;
  const drawX = Math.round(enemy.x - camX + dOff.x);
  const drawY = Math.round(enemy.y - camY + dOff.y);

  // DECISION: GloomBat gets a subtle scaleY pulse keyed to its FLY clip
  // phase. The 5-frame wing cycle (18 fps) looks stiff without it — the
  // scale oscillation smooths the visual transition between discrete frames,
  // giving the illusion of sub-frame interpolation.
  if ((enemy as { entityType?: string }).entityType === 'GloomBat' && cfg.clip.loop) {
    const phase = (anim.frameIndex - cfg.clip.start) / (cfg.clip.end - cfg.clip.start);
    const scaleY = 1 + Math.sin(phase * Math.PI * 2) * 0.08;
    const scaledH = dOff.h * scaleY;
    const offsetY = (dOff.h - scaledH) / 2;
    drawSpriteFrame(ctx, sheet, cfg.sheet.frameWidth, cfg.sheet.frameHeight,
      anim.frameIndex, drawX, drawY + offsetY, dOff.w, scaledH, isFacingRight);
    return;
  }

  // DECISION: Nibbler SQUISHED — flatten vertically, stretch horizontally,
  // and pin the bottom to the ground so it looks like it's pancaked in place.
  if ((enemy as { entityType?: string }).entityType === 'Nibbler' && enemy.state === 'SQUISHED') {
    const p = (enemy as { squishProgress?: number }).squishProgress ?? 0;
    const scaleY = 1 - p * 0.75;          // squash to 25% height
    const scaleX = 1 + p * 0.4;           // stretch to 140% width
    const w = dOff.w * scaleX;
    const h = dOff.h * scaleY;
    const sx = drawX - (w - dOff.w) / 2;     // centre the wider sprite
    const sy = drawY + dOff.h - h;            // pin bottom edge
    ctx.save();
    ctx.globalAlpha = 1 - p * 0.6;               // fade towards end
    drawSpriteFrame(ctx, sheet, cfg.sheet.frameWidth, cfg.sheet.frameHeight,
      anim.frameIndex, sx, sy, w, h, isFacingRight);
    ctx.restore();
    return;
  }

  drawSpriteFrame(
    ctx,
    sheet,
    cfg.sheet.frameWidth,
    cfg.sheet.frameHeight,
    anim.frameIndex,
    drawX,
    drawY,
    dOff.w,
    dOff.h,
    isFacingRight,
  );
}
